import React, { useState, useEffect, useContext, useMemo } from "react";
import { Routes, Route, useParams, useLocation } from "react-router-dom";
import styles from "./Layout.module.scss";
import { useQuery } from "@apollo/client";
import { useMediaQuery } from "@material-ui/core";
import gql from "graphql-tag";
import { useAuth } from "../context/authContext";
import { DialogContext } from "../context/dialogContext";
import { FetchContext } from "../context/fetchContext";
import { SnackbarContext } from "../context/snackbarContext";
import Sidebar from "../components/Sidebar/Sidebar";
import Navbar from "../components/Navbar/Navbar";
import Loading from "../components/Loading/Loading";
import { Inactive, Error } from "../views";
import { ROUTES } from "../routes";
import { isVantageAdmin } from "../utils/authorization";
import _ from "lodash";

import AddPlanDialog from "../components/AddPlanDialog/AddPlanDialog";
import AddEditObjectiveDialog from "../components/AddEditObjectiveDialog/AddEditObjectiveDialog";
import AddEditMetricDialog from "../components/AddEditMetricDialog/AddEditMetricDialog";
import AddEditRockDialog from "../components/AddEditRockDialog/AddEditRockDialog";
import AddEditSuccessCriteriaDialog from "../components/AddEditSuccessCriteriaDialog/AddEditSuccessCriteriaDialog";
import AddEditTodoDialog from "../components/AddEditTodoDialog/AddEditTodoDialog";
import AddWeeklyTargetDialog from "../components/AddWeeklyTargetDialog/AddWeeklyTargetDialog";
import AddEditMeetingDialog from "../components/AddEditMeetingDialog/AddEditMeetingDialog";
import NotesTodosDialog from "../components/Notes/NotesTodosDialog";
import ChatComponent from "../components/ChatComponent/ChatComponent";
import RecentNotesBar from "../components/RecentNotesBar/RecentNotesBar";

const Layout = () => {
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const params = useParams();
  const location = useLocation();

  const { auth } = useAuth();
  const { dialog, setDialog } = useContext(DialogContext);
  const { requestFetch, fetch } = useContext(FetchContext);
  const { snack } = useContext(SnackbarContext);

  const [isSidebarOpen, setIsSidebarOpen] = useState(isDesktop);
  const [isNotesBarOpen, setIsNotesBarOpen] = useState(false);

  const { loading, data: orgData, refetch } = useQuery(GET_ORGANIZATION, { variables: { id: params.org } });

  const userAuth = _.get(auth, "auth");

  const handleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const handleNotesBar = () => {
    setIsNotesBarOpen((prev) => !prev);
  };

  const routesForAuth = useMemo(() => {
    if (!auth) return [];

    return _.filter(ROUTES, (route) => {
      const { layout, path, component } = route;
      return !_.isNil(path) && !_.isNil(component) && _.includes(layout, userAuth);
    });
  }, [userAuth]);

  const errorRoutePaths = useMemo(() => {
    return _(ROUTES)
      .differenceBy(routesForAuth, "name")
      .filter((route) => !_.isNil(route.path))
      .map("path")
      .value();
  }, [routesForAuth]);

  useEffect(() => {
    const isMeetingPage = location.pathname.split("/").includes("meeting");
    if (isDesktop && !isMeetingPage) {
      setIsSidebarOpen(true);
    } else {
      setIsSidebarOpen(false);
      setIsNotesBarOpen(false);
    }
  }, [isDesktop, location]);

  useEffect(() => {
    refetch();
  }, [fetch]);

  const relativePath = _.last(location.pathname.split(params.org));

  if (loading) return <Loading fullscreen size={128} />;

  if (!orgData || !orgData.organization || errorRoutePaths.includes(_.trimStart(relativePath, "/")))
    return <Error title="Unauthorized" body="You do not have permission to proceed" hasNavigation />;

  if (!orgData.organization.active && !isVantageAdmin(auth)) return <Inactive name={orgData.organization.name} />;

  return (
    <>
      <Navbar
        dialog={dialog}
        setDialog={setDialog}
        org={orgData.organization}
        params={params}
        handleSidebar={handleSidebar}
        handleNotesBar={handleNotesBar}
        snack={snack}
        open={isSidebarOpen}
      />
      <Sidebar params={params} org={orgData.organization} open={isSidebarOpen} handleClose={handleSidebar} isDesktop={isDesktop} />
      <div className={!isDesktop ? styles.contentFullscreen : isSidebarOpen ? styles.content : styles.contentFullscreen}>
        <Routes>
          {routesForAuth.map((route) => {
            const { path, name, props, component: Component } = route;

            return <Route path={`${path}`} element={<Component org={orgData.organization} params={params} {...props} />} key={name} />;
          })}
        </Routes>
      </div>
      <RecentNotesBar
        open={isNotesBarOpen}
        handleClose={handleNotesBar}
        auth={auth}
        snack={snack}
        org={orgData.organization}
        fetch={fetch}
      />

      <AddPlanDialog dialog={dialog} setDialog={setDialog} requestFetch={requestFetch} params={params} snack={snack} />
      <AddEditObjectiveDialog
        dialog={dialog}
        setDialog={setDialog}
        requestFetch={requestFetch}
        fetch={fetch}
        params={params}
        org={orgData.organization}
        snack={snack}
      />
      <AddEditMetricDialog
        dialog={dialog}
        setDialog={setDialog}
        requestFetch={requestFetch}
        fetch={fetch}
        params={params}
        org={orgData.organization}
        snack={snack}
      />
      <AddEditRockDialog
        dialog={dialog}
        setDialog={setDialog}
        requestFetch={requestFetch}
        fetch={fetch}
        params={params}
        org={orgData.organization}
        snack={snack}
      />
      <AddEditSuccessCriteriaDialog
        dialog={dialog}
        setDialog={setDialog}
        requestFetch={requestFetch}
        params={params}
        org={orgData.organization}
        snack={snack}
      />
      <AddEditTodoDialog
        dialog={dialog}
        setDialog={setDialog}
        fetch={fetch}
        requestFetch={requestFetch}
        params={params}
        org={orgData.organization}
        snack={snack}
      />
      <AddWeeklyTargetDialog dialog={dialog} setDialog={setDialog} requestFetch={requestFetch} params={params} snack={snack} />
      <AddEditMeetingDialog dialog={dialog} setDialog={setDialog} requestFetch={requestFetch} fetch={fetch} params={params} snack={snack} />

      <NotesTodosDialog snack={snack} dialog={dialog} setDialog={setDialog} fetch={fetch} />

      {auth.id && <ChatComponent userId={auth.id} />}
    </>
  );
};

export default Layout;

const GET_ORGANIZATION = gql`
  query Layout_GetOrg($id: ID!) {
    organization(id: $id) {
      id
      name
      active
      fiscalYear
      profilePicture
      plansOrder
      core {
        purpose
        value
        competency
        market
        offering
        valueProposition
        differentiation
        process
      }
      createdAt
      bhag {
        goal
        targetDate
      }
      viewPreferences {
        name
        position
        show
      }
      notes {
        bhag {
          id
        }
        purpose {
          id
        }
        value {
          id
        }
        competency {
          id
        }
        market {
          id
        }
        offering {
          id
        }
        valueProposition {
          id
        }
        process {
          id
        }
        differentiation {
          id
        }
      }
    }
  }
`;
